var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Dimensions, Keyboard, KeyboardAvoidingView, Modal, Platform, StyleSheet, TextInput, } from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView, } from 'react-native-gesture-handler';
import Animated, { Easing, Extrapolation, interpolate, runOnJS, useAnimatedStyle, useSharedValue, withSpring, withTiming, } from 'react-native-reanimated';
import { SafeAreaProvider, SafeAreaView, useSafeAreaInsets, } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { SccPressable } from '@/components/SccPressable';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { PlaceAiSummaryDownvoteReasonDto } from '@/generated-sources/openapi';
import { LogView } from '@/logging/LogView';
const COMMENT_MAX_LENGTH = 300;
const SCREEN_HEIGHT = Dimensions.get('window').height;
// 등장/스프링백 공통. overshootClamping 으로 열림 위치(translateY=0)를 넘어 더 올라갔다
// 되돌아오는 튕김을 없앤다 — 최고 높이를 초과하지 않고 감속만 한다.
const SHEET_SPRING = {
    damping: 26,
    stiffness: 260,
    mass: 1,
    overshootClamping: true,
};
const EXIT_DURATION_MS = 220;
// 손을 뗀 시점의 속도를 이만큼(초) 미래로 투영한 위치로 닫힘을 판정한다.
// 짧게 툭 튕겨도 닫히고, 천천히 조금 내린 건 안 닫힌다.
const VELOCITY_PROJECTION_SEC = 0.12;
const DISMISS_DISTANCE_RATIO = 0.3;
const DISMISS_VELOCITY = 800;
// 위로 끌 때의 고무줄 저항 계수 — 시트가 제자리 위로 떠오르지 않게 한다.
const OVERDRAG_RESISTANCE = 4;
const REASON_OPTIONS = [
    {
        reason: PlaceAiSummaryDownvoteReasonDto.InaccurateInfo,
        label: '실제와 틀린 정보가 있어요',
    },
    {
        reason: PlaceAiSummaryDownvoteReasonDto.TooLong,
        label: '길어서 읽기 어려워요',
    },
    { reason: PlaceAiSummaryDownvoteReasonDto.Other, label: '기타' },
];
/**
 * 붐따 사유 선택 바텀시트 (Figma `1618873138`).
 * X/배경 탭/아래로 스와이프로 닫으면 입력을 리셋한다 — 제출된 게 아니므로 상위 vote 상태는 바뀌지 않는다.
 * 제출 실패 시엔 리셋하지 않는다(입력 보존) — 에러 토스트는 useAiSummaryFeedback에서 띄운다.
 *
 * 공용 `@/modals/BottomSheet` 대신 시트를 직접 들고 있다. 슬라이드업/드래그 닫기를 이 케이스에만
 * 시범 적용하기 위함 — 공용 컴포넌트를 고치면 소비처 19곳이 한꺼번에 바뀐다. 여기서 검증되면 그때 올린다.
 */
export default function AiSummaryDownvoteBottomSheet({ isVisible, isPending, onPressClose, onSubmit, }) {
    const [selectedReason, setSelectedReason] = useState(null);
    const [comment, setComment] = useState('');
    // https://github.com/facebook/react-native/issues/47140
    // android 에서 translucent status bar 를 키면, keyboard 가 한 번 올라갔다 내려가면 translucent 영역만큼
    // 뷰가 위로 올라간 채로 남는다. 인셋만큼 offset 을 주되 키보드가 올라갔을 때는 패딩으로 보정한다.
    const insets = useSafeAreaInsets();
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const keyboardVerticalOffset = Platform.OS === 'android' ? -insets.bottom - insets.top : 0;
    const temporalContainerPaddingBottom = isKeyboardVisible && Platform.OS === 'android'
        ? insets.bottom + insets.top
        : 0;
    useEffect(() => {
        const showListener = Keyboard.addListener('keyboardDidShow', () => setIsKeyboardVisible(true));
        const hideListener = Keyboard.addListener('keyboardDidHide', () => setIsKeyboardVisible(false));
        return () => {
            showListener.remove();
            hideListener.remove();
        };
    }, []);
    // ── 시트 모션 ─────────────────────────────────────────────────────────
    // translateY: 0 = 완전히 펼쳐짐, sheetHeight = 화면 밖으로 완전히 내려감.
    // dim 투명도도 이 값에서 파생시켜 등장/퇴장/드래그가 하나의 값으로 움직이게 한다.
    const translateY = useSharedValue(SCREEN_HEIGHT);
    const sheetHeight = useSharedValue(SCREEN_HEIGHT);
    const dragStartY = useSharedValue(0);
    // Modal 이 실제로 떠 있는지. 퇴장 애니메이션이 끝날 때까지 isVisible=false 여도 유지한다.
    const [isRendered, setIsRendered] = useState(isVisible);
    const hasEnteredRef = useRef(false);
    useEffect(() => {
        if (!isVisible) {
            return;
        }
        translateY.value = SCREEN_HEIGHT;
        hasEnteredRef.current = false;
        setIsRendered(true);
    }, [isVisible, translateY]);
    useEffect(() => {
        if (isVisible || !isRendered) {
            return;
        }
        translateY.value = withTiming(sheetHeight.value, { duration: EXIT_DURATION_MS, easing: Easing.in(Easing.cubic) }, finished => {
            if (finished) {
                runOnJS(setIsRendered)(false);
            }
        });
    }, [isVisible, isRendered, translateY, sheetHeight]);
    const handleSheetLayout = (event) => {
        sheetHeight.value = event.nativeEvent.layout.height;
        if (hasEnteredRef.current || !isVisible) {
            return;
        }
        // 높이를 알게 된 첫 프레임에 등장시킨다. 그 전까진 화면 밖에 있어 깜빡임이 없다.
        hasEnteredRef.current = true;
        translateY.value = withSpring(0, SHEET_SPRING);
    };
    const handleClose = () => {
        setSelectedReason(null);
        setComment('');
        onPressClose();
    };
    // runOnJS(Keyboard.dismiss) 는 Keyboard 객체째로 UI 런타임에 넘기려다 터진다 —
    // 반드시 평범한 JS 함수로 한 번 감싼다.
    const dismissKeyboard = () => Keyboard.dismiss();
    const panGesture = Gesture.Pan()
        // 아래로 12px 이상 움직였을 때만 활성화 — 라디오/버튼 탭과 TextInput 조작을 방해하지 않는다.
        .activeOffsetY(12)
        .onStart(() => {
        dragStartY.value = translateY.value;
        runOnJS(dismissKeyboard)();
    })
        .onUpdate(event => {
        const next = dragStartY.value + event.translationY;
        translateY.value = next >= 0 ? next : next / OVERDRAG_RESISTANCE;
    })
        .onEnd(event => {
        const projected = translateY.value + event.velocityY * VELOCITY_PROJECTION_SEC;
        const shouldDismiss = projected > sheetHeight.value * DISMISS_DISTANCE_RATIO ||
            event.velocityY > DISMISS_VELOCITY;
        if (shouldDismiss) {
            // 손가락 속도를 그대로 이어받아 내려간다 (툭 튕기면 그만큼 빠르게 사라짐).
            translateY.value = withSpring(sheetHeight.value, Object.assign(Object.assign({}, SHEET_SPRING), { velocity: event.velocityY }), finished => {
                if (finished) {
                    runOnJS(handleClose)();
                }
            });
        }
        else {
            translateY.value = withSpring(0, Object.assign(Object.assign({}, SHEET_SPRING), { velocity: event.velocityY }));
        }
    });
    const sheetStyle = useAnimatedStyle(() => ({
        transform: [{ translateY: translateY.value }],
    }));
    const dimStyle = useAnimatedStyle(() => ({
        opacity: interpolate(translateY.value, [0, sheetHeight.value], [1, 0], Extrapolation.CLAMP),
    }));
    const handleSubmit = () => __awaiter(this, void 0, void 0, function* () {
        if (!selectedReason || isPending) {
            return;
        }
        try {
            yield onSubmit({
                downvoteReason: selectedReason,
                comment: comment.trim() ? comment.trim() : undefined,
            });
            setSelectedReason(null);
            setComment('');
        }
        catch (_a) {
            // 실패 시 입력 보존. 토스트는 onSubmit(useAiSummaryFeedback) 쪽에서 이미 표시했다.
        }
    });
    const isSubmitDisabled = !selectedReason || isPending;
    if (!isRendered) {
        return null;
    }
    return (_jsx(LogView, { elementName: "bottom_sheet", children: _jsx(Modal, { visible: true, transparent: true, animationType: "none", statusBarTranslucent: true, onRequestClose: handleClose, children: _jsx(GestureHandlerRootView, { style: StyleSheet.absoluteFill, children: _jsxs(SafeAreaProvider, { children: [_jsx(Animated.View, { pointerEvents: "none", style: [
                                StyleSheet.absoluteFillObject,
                                { backgroundColor: color.blacka50 },
                                dimStyle,
                            ] }), _jsx(SccTouchableOpacity, { elementName: "bottom_sheet_background", activeOpacity: 1, style: StyleSheet.absoluteFillObject, onPress: () => {
                                Keyboard.dismiss();
                                handleClose();
                            } }), _jsx(KeyboardAvoidingView, { behavior: "padding", style: styles.keyboardAvoider, pointerEvents: "box-none", keyboardVerticalOffset: keyboardVerticalOffset, children: _jsx(GestureDetector, { gesture: panGesture, children: _jsxs(Animated.View, { onLayout: handleSheetLayout, style: sheetStyle, children: [_jsx(SheetTail, {}), _jsx(Sheet, { style: { paddingBottom: temporalContainerPaddingBottom }, children: _jsx(SafeAreaView, { edges: ['bottom'], children: _jsxs(ContentsContainer, { children: [_jsx(Grabber, {}), _jsx(HeaderTitle, { children: "\uC5B4\uB5A4 \uC810\uC774 \uC544\uC26C\uC6B0\uC168\uB098\uC694?" }), _jsx(ReasonList, { accessibilityRole: "radiogroup", children: REASON_OPTIONS.map(option => (_jsxs(ReasonOptionRow, { elementName: "ai_summary_downvote_reason", logParams: { reason: option.reason }, onPress: () => setSelectedReason(option.reason), accessibilityRole: "radio", accessibilityLabel: option.label, accessibilityState: {
                                                                    selected: selectedReason === option.reason,
                                                                    checked: selectedReason === option.reason,
                                                                }, children: [_jsx(RadioCircle, { isSelected: selectedReason === option.reason, children: _jsx(RadioInnerDot, {}) }), _jsx(ReasonLabel, { children: option.label })] }, option.reason))) }), _jsxs(CommentSection, { children: [_jsxs(CommentLabel, { children: ["\uC804\uD558\uACE0 \uC2F6\uC740 \uC758\uACAC\uC774 \uC788\uC73C\uC2E0\uAC00\uC694?", ' ', _jsx(CommentLabelOptional, { children: "(\uC120\uD0DD)" })] }), _jsx(CommentInput, { multiline: true, value: comment, onChangeText: setComment, maxLength: COMMENT_MAX_LENGTH, placeholder: "\uC791\uC131\uD574\uC8FC\uC2E0 \uC758\uACAC\uC740 \uACC4\uB2E8\uBFCC\uC154\uD074\uB7FD\uC5D0 \uD070 \uB3C4\uC6C0\uC774 \uB418\uC5B4\uC694.", placeholderTextColor: color.gray40, style: styles.commentInput }), _jsxs(CommentCounter, { children: [comment.length, "/", COMMENT_MAX_LENGTH] })] }), _jsx(SubmitButton, { elementName: "ai_summary_downvote_submit", disabled: isSubmitDisabled, onPress: handleSubmit, accessibilityRole: "button", accessibilityState: { disabled: isSubmitDisabled }, children: _jsx(SubmitButtonText, { children: "\uC758\uACAC \uBCF4\uB0B4\uAE30" }) })] }) }) })] }) }) })] }) }) }) }));
}
const styles = StyleSheet.create({
    keyboardAvoider: { flex: 1, justifyContent: 'flex-end' },
    commentInput: { minHeight: 134 },
});
const SheetTail = styled.View `
  position: absolute;
  top: 100%;
  /* 100% 가 기기 픽셀 격자에서 반내림되며 시트 바닥과 1px 틈이 생긴다 — 1dp 겹쳐 덮는다. */
  margin-top: -1px;
  left: 0;
  right: 0;
  height: ${SCREEN_HEIGHT}px;
  background-color: ${color.white};
`;
const Sheet = styled.View `
  background-color: ${color.white};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`;
const ContentsContainer = styled.View `
  padding-horizontal: 20px;
  padding-bottom: 24px;
`;
const Grabber = styled.View `
  width: 48px;
  height: 4px;
  border-radius: 2px;
  background-color: #e8e8e8;
  align-self: center;
  margin-top: 8px;
  margin-bottom: 16px;
`;
const HeaderTitle = styled.Text `
  margin-bottom: 24px;
  text-align: center;
  font-family: ${font.pretendardBold};
  font-size: 20px;
  color: ${color.black};
`;
const ReasonList = styled.View `
  gap: 16px;
  margin-bottom: 28px;
`;
const ReasonOptionRow = styled(SccPressable) `
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
// unselected도 selected와 같은 도넛 형태(꽉 찬 링 + 흰 내부 점) — 색만 다르다 (Figma 219:4019 실측).
const RadioCircle = styled.View `
  width: 20px;
  height: 20px;
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  background-color: ${({ isSelected }) => isSelected ? color.brand40 : color.gray30v2};
`;
const RadioInnerDot = styled.View `
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: ${color.white};
`;
const ReasonLabel = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 15px;
  color: ${color.gray90v2};
`;
const CommentSection = styled.View `
  gap: 8px;
  margin-bottom: 24px;
`;
const CommentLabel = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  color: ${color.gray90v2};
`;
const CommentLabelOptional = styled.Text `
  color: ${color.gray70};
`;
const CommentInput = styled(TextInput) `
  font-family: ${font.pretendardRegular};
  font-size: 15px;
  color: ${color.black};
  border-width: 1px;
  border-color: ${color.gray20v2};
  border-radius: 8px;
  padding: 12px;
  text-align-vertical: top;
`;
const CommentCounter = styled.Text `
  align-self: flex-end;
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  color: ${color.gray50};
`;
const SubmitButton = styled(SccPressable) `
  height: 56px;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  background-color: ${({ disabled }) => disabled ? color.gray20v2 : color.brand40};
`;
const SubmitButtonText = styled.Text `
  font-family: ${font.pretendardBold};
  font-size: 16px;
  color: ${color.white};
`;
